import { useLayoutEffect, useRef } from 'react'
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

export interface PeriodBarSeries {
    /** dataKey trong `rows`. */
    key: string
    name: string
    color: string
}

export interface PeriodBarChartProps {
    /** Mô tả cho trình đọc màn hình (tên biểu đồ + khoảng thời gian). */
    ariaLabel: string
    rows: Array<{ period: string } & Record<string, number | string>>
    series: PeriodBarSeries[]
    /** Nhãn ngắn trên trục X từ `period` (ngày đầu kỳ). */
    axisLabel: (period: string) => string
    /** Khoảng ngày đầy đủ của kỳ, hiện ở tooltip. */
    rangeLabel: (period: string) => string
    formatValue: (value: number) => string
    /** Rút gọn số trên trục Y (vd 1.500.000 → 1,5 tr). Mặc định = formatValue. */
    formatAxisValue?: (value: number) => string
    /** In số (dạng rút gọn của trục) ngay trên đầu mỗi cột khác 0 — cho người
     *  không quen dóng sang trục để đọc. */
    showValueLabels?: boolean
}

/** Độ rộng tối thiểu của một kỳ: đủ chỗ cho mỗi chuỗi một cột ~14px + khe. */
const PX_PER_BAR = 14
const PX_PER_PERIOD_GAP = 28
/** Sàn bề rộng một kỳ: đủ cho nhãn dài nhất "10/2026" ở cỡ chữ trục 14px
 *  (~55px). Thiếu sàn này thì biểu đồ một chuỗi (Bao, Chai, Tiền) xếp 12 nhãn
 *  tháng chồng lên nhau. */
const MIN_PX_PER_PERIOD = 64

interface TooltipEntry {
    dataKey?: string | number
    value?: number | string
}

export interface PeriodBarTooltipProps {
    active?: boolean
    payload?: TooltipEntry[]
    label?: string
    series: PeriodBarSeries[]
    rangeLabel: (period: string) => string
    formatValue: (value: number) => string
}

/** Tooltip: khoảng ngày của kỳ + giá trị từng chuỗi. Chữ dùng màu chữ, màu
 *  chuỗi chỉ nằm ở ô vuông nhỏ cạnh tên. */
export function PeriodBarTooltip({
    active,
    payload,
    label,
    series,
    rangeLabel,
    formatValue,
}: PeriodBarTooltipProps) {
    if (!active || !payload?.length || !label) return null
    const byKey = new Map(payload.map((p) => [String(p.dataKey), p.value]))
    return (
        <div className="rounded-md border bg-background px-3 py-2 text-base shadow-sm">
            <p className="mb-1 font-medium">{rangeLabel(label)}</p>
            {series.map((s) => (
                <p key={s.key} className="flex items-center gap-2">
                    <span
                        aria-hidden
                        className="size-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: s.color }}
                    />
                    <span className="text-muted-foreground">{s.name}:</span>
                    <span className="font-medium tabular-nums">
                        {formatValue(Number(byKey.get(s.key) ?? 0))}
                    </span>
                </p>
            ))}
        </div>
    )
}

/** Chú giải bằng chữ (≥ 2 chuỗi): danh tính không chỉ dựa vào màu. */
export function PeriodBarLegend({ series }: { series: PeriodBarSeries[] }) {
    if (series.length < 2) return null
    return (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-base">
            {series.map((s) => (
                <li key={s.key} className="flex items-center gap-1.5">
                    <span
                        aria-hidden
                        className="size-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: s.color }}
                    />
                    {s.name}
                </li>
            ))}
        </ul>
    )
}

/** Độ rộng cột trục Y đứng yên bên trái khung cuộn. */
const Y_AXIS_WIDTH = 56
const CHART_HEIGHT = 240
const CHART_MARGIN = { top: 22, right: 8, bottom: 0, left: 0 }

/**
 * Thang chia tròn từ 0 phủ được `max` (~4 khoảng, bước 1/2/2,5/5 × 10ⁿ). Trục Y
 * đứng yên và phần cột cuộn là HAI biểu đồ, nên phải dùng chung một thang tính
 * sẵn — để recharts tự chia thì hai bên có thể lệch nhau.
 */
export function niceTicks(max: number): number[] {
    const top = Number.isFinite(max) && max > 0 ? max : 1
    const raw = top / 4
    const mag = 10 ** Math.floor(Math.log10(raw))
    const step =
        [1, 2, 2.5, 5, 10].map((f) => f * mag).find((c) => c >= raw - 1e-12) ??
        10 * mag
    const count = Math.ceil(top / step - 1e-9)
    return Array.from(
        { length: count + 1 },
        (_, i) => Math.round(i * step * 1e6) / 1e6,
    )
}

/**
 * Biểu đồ cột theo kỳ, mỗi chuỗi một cột cạnh nhau (KHÔNG chồng: các chuỗi là
 * những thứ khác nhau, cộng dồn sẽ đọc ra một tổng vô nghĩa). Trên điện thoại
 * nhiều kỳ không vừa màn: khung tự cuộn ngang thay vì ép cột mảnh như sợi chỉ.
 */
export function PeriodBarChart({
    ariaLabel,
    rows,
    series,
    axisLabel,
    rangeLabel,
    formatValue,
    formatAxisValue,
    showValueLabels = false,
}: PeriodBarChartProps) {
    const minWidth =
        rows.length *
        Math.max(
            MIN_PX_PER_PERIOD,
            Math.max(series.length, 1) * PX_PER_BAR + PX_PER_PERIOD_GAP,
        )
    let max = 0
    for (const row of rows) {
        for (const s of series) max = Math.max(max, Number(row[s.key] ?? 0))
    }
    const ticks = niceTicks(max)
    const domain: [number, number] = [0, ticks[ticks.length - 1]]
    const axisFormatter = (v: number) => (formatAxisValue ?? formatValue)(v)
    // Khung hẹp hơn số kỳ (điện thoại): mở ra phải thấy kỳ HIỆN TẠI ở mép phải,
    // không phải kỳ cũ nhất. Cuộn lại khi khoảng kỳ hoặc độ rộng đổi.
    const scrollRef = useRef<HTMLDivElement>(null)
    const scrollKey = `${rows[0]?.period ?? ''}|${rows.length}|${minWidth}`
    useLayoutEffect(() => {
        const el = scrollRef.current
        if (el) el.scrollLeft = el.scrollWidth
    }, [scrollKey])
    return (
        <div className="space-y-3">
            <PeriodBarLegend series={series} />
            <div className="flex">
                {/* Trục Y đứng yên: cuộn sang kỳ mới nhất vẫn đọc được độ lớn.
                    XAxis ẩn giữ đúng chiều cao vùng nhãn để hai bên thẳng hàng. */}
                <div aria-hidden className="shrink-0">
                    <BarChart
                        // Chừa 4px vùng vẽ: vùng vẽ rộng 0 thì recharts bỏ
                        // luôn cả trục.
                        width={Y_AXIS_WIDTH + 4}
                        height={CHART_HEIGHT}
                        data={rows}
                        margin={{ ...CHART_MARGIN, right: 0 }}
                    >
                        <XAxis
                            dataKey="period"
                            tick={false}
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis
                            width={Y_AXIS_WIDTH}
                            ticks={ticks}
                            domain={domain}
                            interval={0}
                            tick={{ fontSize: 14 }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={axisFormatter}
                            className="text-muted-foreground"
                        />
                        {/* Recharts 3 không vẽ trục nếu biểu đồ không có
                            chuỗi nào; thang đã cố định nên cột trong suốt
                            này không ảnh hưởng gì. */}
                        {series[0] && (
                            <Bar
                                dataKey={series[0].key}
                                fill="transparent"
                                isAnimationActive={false}
                            />
                        )}
                    </BarChart>
                </div>
                <div
                    ref={scrollRef}
                    role="img"
                    aria-label={ariaLabel}
                    className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain pb-1"
                >
                    <div style={{ minWidth }}>
                        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                            <BarChart
                                data={rows}
                                margin={CHART_MARGIN}
                                barGap={2}
                                barCategoryGap="20%"
                            >
                                <CartesianGrid
                                    vertical={false}
                                    strokeDasharray="3 3"
                                    className="stroke-muted"
                                />
                                <XAxis
                                    dataKey="period"
                                    tickFormatter={axisLabel}
                                    interval={0}
                                    tick={{ fontSize: 14 }}
                                    tickLine={false}
                                    className="text-muted-foreground"
                                />
                                <YAxis hide ticks={ticks} domain={domain} />
                                <Tooltip
                                    cursor={{
                                        fill: 'currentColor',
                                        fillOpacity: 0.05,
                                    }}
                                    content={
                                        <PeriodBarTooltip
                                            series={series}
                                            rangeLabel={rangeLabel}
                                            formatValue={formatValue}
                                        />
                                    }
                                />
                                {series.map((s) => (
                                    <Bar
                                        key={s.key}
                                        dataKey={s.key}
                                        name={s.name}
                                        fill={s.color}
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={28}
                                        isAnimationActive={false}
                                    >
                                        {showValueLabels && (
                                            <LabelList
                                                dataKey={s.key}
                                                position="top"
                                                fontSize={14}
                                                fontWeight={600}
                                                className="fill-foreground"
                                                formatter={(v: unknown) => {
                                                    const n = Number(v)
                                                    return Number.isFinite(n) &&
                                                        n > 0
                                                        ? axisFormatter(n)
                                                        : ''
                                                }}
                                            />
                                        )}
                                    </Bar>
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    )
}
